import { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { TaskService } from 'service/task.service';
import { TaskFilters } from '../components/TasksFilters/TasksFilters';

type TaskData = {
  totalRecords: number;
  records: any[];
  totalFilteredRecords: number;
};

type TasksData = {
  toDo: TaskData;
  inProgress: TaskData;
  done: TaskData;
};

export const useTasksData = (mine: boolean, filters?: TaskFilters) => {
  const params = useParams();
  const [tasksData, setTasksData] = useState<TasksData>({
    toDo: { totalRecords: 0, records: [], totalFilteredRecords: 0 },
    inProgress: { totalRecords: 0, records: [], totalFilteredRecords: 0 },
    done: { totalRecords: 0, records: [], totalFilteredRecords: 0 },
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const buildQueryParams = useCallback(() => {
    const queryParams: any = {
      mine: mine,
    };

    if (params?.id) {
      queryParams.client = params.id;
    }

    if (filters) {
      if (filters.priority && filters.priority.length > 0) {
        queryParams.priority = filters.priority.join(',');
      }

      if (filters.client && filters.client.length > 0) {
        queryParams.client = filters.client.join(',');
      }

      if (filters.recordTypes && filters.recordTypes.length > 0) {
        queryParams.recordType = filters.recordTypes.join(',');
      }

      if (filters.assignedTo && filters.assignedTo.length > 0) {
        queryParams.assignedTo = filters.assignedTo.join(',');
      }

      if (filters.search && filters.search.trim()) {
        queryParams.search = filters.search.trim();
      }

      if (filters.needAttention) {
        queryParams.needAttention = true;
      }

      if (filters.time && filters.time.length > 0) {
        queryParams.dueDate = filters.time[0];
      }
    }

    return queryParams;
  }, [mine, filters, params?.id]);

  const fetchTasks = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const queryParams = buildQueryParams();
      const response = await TaskService.getKanban(queryParams);

      const toDoTasks = response.data?.['To Do'] || [];
      const inProgressTasks = response.data?.['In Progress'] || [];
      const doneTasks = response.data?.['Done'] || [];

      setTasksData({
        toDo: {
          totalRecords: toDoTasks.length,
          records: toDoTasks,
          totalFilteredRecords: toDoTasks.length,
        },
        inProgress: {
          totalRecords: inProgressTasks.length,
          records: inProgressTasks,
          totalFilteredRecords: inProgressTasks.length,
        },
        done: {
          totalRecords: doneTasks.length,
          records: doneTasks,
          totalFilteredRecords: doneTasks.length,
        },
      });
    } catch (err: any) {
      console.error('Failed to fetch tasks:', err);
      setError(err?.message || 'Failed to fetch tasks');
      setTasksData({
        toDo: { totalRecords: 0, records: [], totalFilteredRecords: 0 },
        inProgress: { totalRecords: 0, records: [], totalFilteredRecords: 0 },
        done: { totalRecords: 0, records: [], totalFilteredRecords: 0 },
      });
    } finally {
      setLoading(false);
    }
  }, [buildQueryParams]);

  useEffect(() => {
    fetchTasks();
  }, [fetchTasks]);

  return {
    tasksData,
    loading,
    error,
    refetch: fetchTasks,
  };
};
